import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import BopRowActions from '../../components/BopRowActions';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { API_DOCS } from './IntegrazioneOpenApiPage.api';

// ── Tipi ────────────────────────────────────────────────────────────────────
type Ambiente = 'SANDBOX' | 'PRODUZIONE';

interface ServizioCatalogo {
  codice: string;
  etichetta: string;
  descrizione: string | null;
  urlProduzioneTemplate: string;
  urlSandboxTemplate: string;
  scopeRichiesto: string;
  numeroCrediti: number;
  attivoNelCatalogo: boolean;
}

interface Servizio {
  id: number;
  societaId: number;
  configurazioneId: number;
  codiceCatalogo: string;
  urlProduzione: string;
  urlSandbox: string;
  tokenProduzione: string | null;
  tokenSandbox: string | null;
  tokenIdProduzione: string | null;
  tokenIdSandbox: string | null;
  tokenScadenzaProduzione: string | null;
  tokenScadenzaSandbox: string | null;
  ultimaVerificaProduzione: string | null;
  ultimaVerificaSandbox: string | null;
  esitoVerificaProduzione: boolean | null;
  esitoVerificaSandbox: boolean | null;
  attivo: boolean;
  note: string | null;
  catalogo: ServizioCatalogo;
}

interface Configurazione {
  id: number;
  societaId: number;
  ambienteAttivo: Ambiente;
  emailAccount: string | null;
  apiKeyProduzione: string | null;
  apiKeySandbox: string | null;
  creditoUltimo: number | null;
  creditoUltimoAt: string | null;
  creditoAmbienteUltimo: Ambiente | null;
  attivo: boolean;
  note: string | null;
  servizi: Servizio[];
}

interface FormHeader {
  ambienteAttivo: Ambiente;
  emailAccount: string;
  apiKeyProduzione: string;
  apiKeySandbox: string;
  attivo: boolean;
  note: string;
}

interface FormServizio {
  id?: number;
  codiceCatalogo: string;
  urlProduzione: string;
  urlSandbox: string;
  attivo: boolean;
  note: string;
}

interface LogEntry {
  id: number;
  societaId: number;
  servizio: string;
  ambiente: Ambiente;
  codice: string;
  httpStatus: number | null;
  durataMs: number | null;
  esitoOk: boolean;
  fontecache: boolean;
  erroreMessaggio: string | null;
  utenteId: number | null;
  createdAt: string;
}

interface VerificaCredenzialiResp {
  ok: boolean;
  latenzaMs: number;
  httpStatus: number;
  ambiente: Ambiente;
  creditoResiduo: number | null;
  messaggio: string;
}

interface KeepaliveResp {
  ok: boolean;
  latenzaMs: number;
  httpStatus: number;
  ambiente: Ambiente;
  messaggio: string;
}

interface GeneraTokenResp {
  ok: boolean;
  latenzaMs: number;
  httpStatus: number;
  ambiente: Ambiente;
  scadenza: string | null;
  messaggio: string;
}

const FORM_HEADER_VUOTO: FormHeader = {
  ambienteAttivo: 'SANDBOX',
  emailAccount: '',
  apiKeyProduzione: '',
  apiKeySandbox: '',
  attivo: false,
  note: '',
};

function configToFormHeader(c: Configurazione | null): FormHeader {
  if (!c) return { ...FORM_HEADER_VUOTO };
  return {
    ambienteAttivo: c.ambienteAttivo || 'SANDBOX',
    emailAccount: c.emailAccount || '',
    apiKeyProduzione: c.apiKeyProduzione || '',
    apiKeySandbox: c.apiKeySandbox || '',
    attivo: !!c.attivo,
    note: c.note || '',
  };
}

function formatDateTime(s: string | null | undefined): string {
  if (!s) return '';
  const d = new Date(s);
  return d.toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDateOnly(s: string | null | undefined): string {
  if (!s) return '';
  const d = new Date(s);
  return d.toLocaleDateString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

// ── Stato badge token in base a scadenza ────────────────────────────────────
function tokenBadgeInfo(tokenId: string | null, scadenza: string | null): {
  variant: 'valido' | 'warning' | 'scaduto' | 'nogen';
  label: string;
  date: string;
} {
  if (!tokenId || !scadenza) {
    return { variant: 'nogen', label: 'Non generato', date: '' };
  }
  const now = Date.now();
  const exp = new Date(scadenza).getTime();
  const giorni30 = 30 * 24 * 60 * 60 * 1000;
  const dateStr = formatDateOnly(scadenza);
  if (exp < now) return { variant: 'scaduto', label: 'Scaduto', date: dateStr };
  if (exp < now + giorni30) return { variant: 'warning', label: 'In scadenza', date: dateStr };
  return { variant: 'valido', label: 'Valido', date: dateStr };
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente principale
// ─────────────────────────────────────────────────────────────────────────────
export default function IntegrazioneOpenApiPage() {
  const {
    societaId: filterSocietaId,
    setSocietaId: setFilterSocietaId,
    listaSocieta: societaList,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  const [activeTab, setActiveTab] = useState<'config' | 'log'>('config');

  // ── Configurazione ─────────────────────────────────────────────────────
  const [config, setConfig] = useState<Configurazione | null>(null);
  const [formHeader, setFormHeader] = useState<FormHeader>({ ...FORM_HEADER_VUOTO });
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingHeader, setSavingHeader] = useState(false);
  const [verificaInCorso, setVerificaInCorso] = useState(false);
  const [verificaResult, setVerificaResult] = useState<VerificaCredenzialiResp | null>(null);
  const [headerMessage, setHeaderMessage] = useState('');
  const [headerError, setHeaderError] = useState('');

  // ── Catalogo + Servizi ─────────────────────────────────────────────────
  const [catalogo, setCatalogo] = useState<ServizioCatalogo[]>([]);
  const [serviziError, setServiziError] = useState('');
  const [serviziMessage, setServiziMessage] = useState('');
  const [busyServizioId, setBusyServizioId] = useState<number | null>(null);

  // ── Modale servizio ────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [formServizio, setFormServizio] = useState<FormServizio>({
    codiceCatalogo: '',
    urlProduzione: '',
    urlSandbox: '',
    attivo: true,
    note: '',
  });
  const [savingServizio, setSavingServizio] = useState(false);
  const [modalError, setModalError] = useState('');

  // ── Tab Log ────────────────────────────────────────────────────────────
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [loadingLog, setLoadingLog] = useState(false);
  const [logFilters, setLogFilters] = useState({ dal: '', al: '' });
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });

  // ── Carica configurazione (incluso array servizi) ──────────────────────
  const loadConfigurazione = useCallback(async () => {
    if (!pronta || !filterSocietaId) {
      setConfig(null);
      setFormHeader({ ...FORM_HEADER_VUOTO });
      return;
    }
    try {
      setLoadingConfig(true);
      setHeaderError('');
      setHeaderMessage('');
      setVerificaResult(null);
      // @api GET /api/impostazioni/openapi/configurazione?societaId=N -> { success: true, data: Configurazione | null }
      const resp = await apiFetch<{ success: boolean; data: Configurazione | null }>(
        `/api/impostazioni/openapi/configurazione?societaId=${filterSocietaId}`
      );
      setConfig(resp.data);
      setFormHeader(configToFormHeader(resp.data));
    } catch (err: any) {
      console.error('[IntegrazioneOpenApi] loadConfigurazione', err);
      setHeaderError(err?.message || 'Errore nel caricamento della configurazione');
    } finally {
      setLoadingConfig(false);
    }
  }, [pronta, filterSocietaId]);

  // ── Carica catalogo (una volta) ────────────────────────────────────────
  const loadCatalogo = useCallback(async () => {
    try {
      // @api GET /api/impostazioni/openapi/servizi-catalogo -> { success: true, data: ServizioCatalogo[] }
      const resp = await apiFetch<{ success: boolean; data: ServizioCatalogo[] }>(
        '/api/impostazioni/openapi/servizi-catalogo'
      );
      setCatalogo(resp.data || []);
    } catch (err: any) {
      console.error('[IntegrazioneOpenApi] loadCatalogo', err);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'config') {
      loadConfigurazione();
      loadCatalogo();
    }
  }, [activeTab, loadConfigurazione, loadCatalogo]);

  // ── Carica log ─────────────────────────────────────────────────────────
  const loadLog = useCallback(async () => {
    if (!pronta || !filterSocietaId) {
      setLogEntries([]);
      return;
    }
    try {
      setLoadingLog(true);
      const params = new URLSearchParams();
      params.append('societaId', filterSocietaId);
      if (logFilters.dal) params.append('dal', logFilters.dal);
      if (logFilters.al) params.append('al', logFilters.al);
      params.append('page', String(pagination.page));
      params.append('limit', String(pagination.limit));

      // @api GET /api/impostazioni/openapi/log?societaId=N&dal=&al=&page=1&limit=50 -> { success: true, data: { log: LogEntry[], pagination: { page, limit, total, totalPages } } }
      const resp = await apiFetch<{
        success: boolean;
        data: { log: LogEntry[]; pagination: typeof pagination };
      }>(`/api/impostazioni/openapi/log?${params}`);

      setLogEntries(resp.data.log);
      setPagination(resp.data.pagination);
    } catch (err: any) {
      console.error('[IntegrazioneOpenApi] loadLog', err);
    } finally {
      setLoadingLog(false);
    }
  }, [pronta, filterSocietaId, logFilters.dal, logFilters.al, pagination.page, pagination.limit]);

  useEffect(() => {
    if (activeTab === 'log') {
      loadLog();
    }
  }, [activeTab, loadLog]);

  // ── Salva header ───────────────────────────────────────────────────────
  const handleSaveHeader = async () => {
    if (!filterSocietaId) {
      setHeaderError('Seleziona una società');
      return;
    }
    try {
      setSavingHeader(true);
      setHeaderError('');
      setHeaderMessage('');
      const payload = {
        societaId: parseInt(filterSocietaId, 10),
        ambienteAttivo: formHeader.ambienteAttivo,
        emailAccount: formHeader.emailAccount.trim() || null,
        apiKeyProduzione: formHeader.apiKeyProduzione.trim() || null,
        apiKeySandbox: formHeader.apiKeySandbox.trim() || null,
        attivo: formHeader.attivo,
        note: formHeader.note.trim() || null,
      };
      // @api PUT /api/impostazioni/openapi/configurazione { societaId, ambienteAttivo, emailAccount, apiKeyProduzione, apiKeySandbox, attivo, note } -> { success: true, data: Configurazione }
      const resp = await apiFetch<{ success: boolean; data: Configurazione }>(
        '/api/impostazioni/openapi/configurazione',
        {
          method: 'PUT',
          body: JSON.stringify(payload),
        }
      );
      setConfig(resp.data);
      setFormHeader(configToFormHeader(resp.data));
      setHeaderMessage('Configurazione salvata correttamente.');
    } catch (err: any) {
      setHeaderError(err?.message || 'Errore nel salvataggio');
    } finally {
      setSavingHeader(false);
    }
  };

  // ── Verifica credenziali e leggi credito ───────────────────────────────
  const handleVerificaCredenziali = async () => {
    if (!filterSocietaId) {
      setHeaderError('Seleziona una società');
      return;
    }
    try {
      setVerificaInCorso(true);
      setVerificaResult(null);
      setHeaderError('');
      // @api POST /api/impostazioni/openapi/configurazione/verifica-credenziali { societaId, ambiente? } -> { success: true, data: { ok, latenzaMs, httpStatus, ambiente, creditoResiduo, messaggio } }
      const resp = await apiFetch<{ success: boolean; data: VerificaCredenzialiResp }>(
        '/api/impostazioni/openapi/configurazione/verifica-credenziali',
        {
          method: 'POST',
          body: JSON.stringify({
            societaId: parseInt(filterSocietaId, 10),
            ambiente: formHeader.ambienteAttivo,
          }),
        }
      );
      setVerificaResult(resp.data);
      // Ricarica configurazione per aggiornare creditoUltimo*
      await loadConfigurazione();
    } catch (err: any) {
      setHeaderError(err?.message || 'Errore nella verifica credenziali');
    } finally {
      setVerificaInCorso(false);
    }
  };

  // ── Apertura modale ────────────────────────────────────────────────────
  const apriModaleAdd = () => {
    setModalMode('add');
    setModalError('');
    setFormServizio({
      codiceCatalogo: '',
      urlProduzione: '',
      urlSandbox: '',
      attivo: true,
      note: '',
    });
    setModalOpen(true);
  };

  const apriModaleEdit = (s: Servizio) => {
    setModalMode('edit');
    setModalError('');
    setFormServizio({
      id: s.id,
      codiceCatalogo: s.codiceCatalogo,
      urlProduzione: s.urlProduzione || '',
      urlSandbox: s.urlSandbox || '',
      attivo: s.attivo,
      note: s.note || '',
    });
    setModalOpen(true);
  };

  // Quando cambia codiceCatalogo in modalità add, precompila gli URL dal template
  const onChangeCodiceCatalogo = (codice: string) => {
    const item = catalogo.find((c) => c.codice === codice);
    setFormServizio((prev) => ({
      ...prev,
      codiceCatalogo: codice,
      urlProduzione: item ? item.urlProduzioneTemplate : '',
      urlSandbox: item ? item.urlSandboxTemplate : '',
    }));
  };

  // ── Salva servizio (add/edit) ─────────────────────────────────────────
  const handleSaveServizio = async () => {
    if (!filterSocietaId) return;
    if (modalMode === 'add' && !formServizio.codiceCatalogo) {
      setModalError('Seleziona un servizio dal catalogo');
      return;
    }
    try {
      setSavingServizio(true);
      setModalError('');
      if (modalMode === 'add') {
        const payload = {
          societaId: parseInt(filterSocietaId, 10),
          codiceCatalogo: formServizio.codiceCatalogo,
          urlProduzione: formServizio.urlProduzione.trim() || undefined,
          urlSandbox: formServizio.urlSandbox.trim() || undefined,
          attivo: formServizio.attivo,
          note: formServizio.note.trim() || undefined,
        };
        // @api POST /api/impostazioni/openapi/servizi { societaId, codiceCatalogo, urlProduzione?, urlSandbox?, attivo?, note? } -> { success: true, data: Servizio }
        await apiFetch<{ success: boolean; data: Servizio }>(
          '/api/impostazioni/openapi/servizi',
          { method: 'POST', body: JSON.stringify(payload) }
        );
      } else if (formServizio.id) {
        const payload = {
          urlProduzione: formServizio.urlProduzione.trim(),
          urlSandbox: formServizio.urlSandbox.trim(),
          attivo: formServizio.attivo,
          note: formServizio.note.trim() || null,
        };
        // @api PUT /api/impostazioni/openapi/servizi/:id { urlProduzione?, urlSandbox?, attivo?, note? } -> { success: true, data: Servizio }
        await apiFetch<{ success: boolean; data: Servizio }>(
          `/api/impostazioni/openapi/servizi/${formServizio.id}`,
          { method: 'PUT', body: JSON.stringify(payload) }
        );
      }
      setModalOpen(false);
      setServiziMessage(modalMode === 'add' ? 'Servizio aggiunto.' : 'Servizio aggiornato.');
      await loadConfigurazione();
    } catch (err: any) {
      setModalError(err?.message || 'Errore nel salvataggio del servizio');
    } finally {
      setSavingServizio(false);
    }
  };

  // ── Elimina servizio ───────────────────────────────────────────────────
  const handleDeleteServizio = async (s: Servizio) => {
    if (!window.confirm(`Eliminare il servizio "${s.catalogo.etichetta}"?`)) return;
    try {
      setBusyServizioId(s.id);
      setServiziError('');
      setServiziMessage('');
      // @api DELETE /api/impostazioni/openapi/servizi/:id -> { success: true }
      await apiFetch<{ success: boolean }>(
        `/api/impostazioni/openapi/servizi/${s.id}`,
        { method: 'DELETE' }
      );
      setServiziMessage('Servizio eliminato.');
      await loadConfigurazione();
    } catch (err: any) {
      setServiziError(err?.message || 'Errore eliminazione servizio');
    } finally {
      setBusyServizioId(null);
    }
  };

  // ── Keepalive ──────────────────────────────────────────────────────────
  const handleKeepalive = async (s: Servizio, ambiente: Ambiente) => {
    try {
      setBusyServizioId(s.id);
      setServiziError('');
      setServiziMessage('');
      // @api POST /api/impostazioni/openapi/servizi/:id/keepalive { ambiente? } -> { success: true, data: { ok, latenzaMs, httpStatus, ambiente, messaggio } }
      const resp = await apiFetch<{ success: boolean; data: KeepaliveResp }>(
        `/api/impostazioni/openapi/servizi/${s.id}/keepalive`,
        { method: 'POST', body: JSON.stringify({ ambiente }) }
      );
      setServiziMessage(
        resp.data.ok
          ? `Keepalive ${ambiente} OK (${resp.data.latenzaMs}ms): ${resp.data.messaggio}`
          : `Keepalive ${ambiente} fallito: ${resp.data.messaggio}`
      );
      await loadConfigurazione();
    } catch (err: any) {
      setServiziError(err?.message || 'Errore keepalive');
    } finally {
      setBusyServizioId(null);
    }
  };

  // ── Genera token ───────────────────────────────────────────────────────
  const handleGeneraToken = async (s: Servizio, ambiente: Ambiente) => {
    try {
      setBusyServizioId(s.id);
      setServiziError('');
      setServiziMessage('');
      // @api POST /api/impostazioni/openapi/servizi/:id/genera-token { ambiente?, ttlGiorni? } -> { success: true, data: { ok, latenzaMs, httpStatus, ambiente, scadenza, messaggio } }
      const resp = await apiFetch<{ success: boolean; data: GeneraTokenResp }>(
        `/api/impostazioni/openapi/servizi/${s.id}/genera-token`,
        { method: 'POST', body: JSON.stringify({ ambiente }) }
      );
      setServiziMessage(
        resp.data.ok
          ? `Token ${ambiente} generato (scadenza ${formatDateOnly(resp.data.scadenza)}): ${resp.data.messaggio}`
          : `Generazione token ${ambiente} fallita: ${resp.data.messaggio}`
      );
      await loadConfigurazione();
    } catch (err: any) {
      setServiziError(err?.message || 'Errore generazione token');
    } finally {
      setBusyServizioId(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="bop-page-header">
        <div>
          <h1 className="bop-page-title">Integrazione OpenApi</h1>
          <p className="bop-text-muted">
            Configurazione servizio OpenApi.com per arricchimento anagrafiche da P.IVA / Codice Fiscale
          </p>
        </div>
      </div>

      {/* Filtro Società */}
      {!isUnica && (
        <div className="bop-filter-bar">
          <div className="openapi-societa-filter__field">
            <label className="bop-label">Società</label>
            <select
              className="bop-select"
              value={filterSocietaId}
              onChange={(e) => setFilterSocietaId(e.target.value)}
            >
              <option value="">Seleziona società...</option>
              {societaList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nome}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Stato vuoto */}
      {!filterSocietaId && (
        <div className="bop-card">
          <div className="bop-empty-state">
            <p className="bop-empty-state__title">Seleziona una società</p>
            <p>Per configurare l'integrazione OpenApi seleziona prima una società.</p>
          </div>
        </div>
      )}

      {/* Tab + contenuto */}
      {filterSocietaId && (
        <>
          <div className="bop-tabs">
            <button
              onClick={() => setActiveTab('config')}
              className={`bop-tab ${activeTab === 'config' ? 'active' : ''}`}
            >
              Configurazione
            </button>
            <button
              onClick={() => setActiveTab('log')}
              className={`bop-tab ${activeTab === 'log' ? 'active' : ''}`}
            >
              Log chiamate
            </button>
          </div>

          {activeTab === 'config' && (
            <SezioneConfigurazione
              loading={loadingConfig}
              config={config}
              formHeader={formHeader}
              setFormHeader={setFormHeader}
              savingHeader={savingHeader}
              headerMessage={headerMessage}
              headerError={headerError}
              onSaveHeader={handleSaveHeader}
              verificaInCorso={verificaInCorso}
              verificaResult={verificaResult}
              onVerifica={handleVerificaCredenziali}
              serviziError={serviziError}
              serviziMessage={serviziMessage}
              busyServizioId={busyServizioId}
              onAdd={apriModaleAdd}
              onEdit={apriModaleEdit}
              onDelete={handleDeleteServizio}
              onKeepalive={handleKeepalive}
              onGeneraToken={handleGeneraToken}
            />
          )}

          {activeTab === 'log' && (
            <SezioneLog
              loading={loadingLog}
              logEntries={logEntries}
              logFilters={logFilters}
              setLogFilters={setLogFilters}
              pagination={pagination}
              setPagination={setPagination}
            />
          )}
        </>
      )}

      {/* Modale aggiungi/modifica servizio */}
      {modalOpen && (
        <ModaleServizio
          mode={modalMode}
          form={formServizio}
          setForm={setFormServizio}
          onChangeCodiceCatalogo={onChangeCodiceCatalogo}
          catalogo={catalogo}
          saving={savingServizio}
          error={modalError}
          onClose={() => setModalOpen(false)}
          onSave={handleSaveServizio}
        />
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sezione Configurazione (Zone 1 + 2)
// ─────────────────────────────────────────────────────────────────────────────
interface SezioneConfigProps {
  loading: boolean;
  config: Configurazione | null;
  formHeader: FormHeader;
  setFormHeader: React.Dispatch<React.SetStateAction<FormHeader>>;
  savingHeader: boolean;
  headerMessage: string;
  headerError: string;
  onSaveHeader: () => void;
  verificaInCorso: boolean;
  verificaResult: VerificaCredenzialiResp | null;
  onVerifica: () => void;
  serviziError: string;
  serviziMessage: string;
  busyServizioId: number | null;
  onAdd: () => void;
  onEdit: (s: Servizio) => void;
  onDelete: (s: Servizio) => void;
  onKeepalive: (s: Servizio, ambiente: Ambiente) => void;
  onGeneraToken: (s: Servizio, ambiente: Ambiente) => void;
}

function SezioneConfigurazione({
  loading, config, formHeader, setFormHeader,
  savingHeader, headerMessage, headerError, onSaveHeader,
  verificaInCorso, verificaResult, onVerifica,
  serviziError, serviziMessage, busyServizioId,
  onAdd, onEdit, onDelete, onKeepalive, onGeneraToken,
}: SezioneConfigProps) {
  const update = <K extends keyof FormHeader>(key: K, value: FormHeader[K]) => {
    setFormHeader((prev) => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="bop-card">
        <div className="ai-loading">
          <p className="ai-loading-text">Caricamento configurazione...</p>
        </div>
      </div>
    );
  }

  const servizi = config?.servizi || [];

  return (
    <>
      {/* ── Zona 1: Header credenziali ─────────────────────────────────── */}
      <div className="bop-card">
        <div className="bop-card__header">
          <h3 className="bop-card__title">Credenziali OpenApi</h3>
        </div>
        <div className="bop-card__body">
          <div className="bop-form-group">
            <label className="ai-checkbox-label">
              <input
                type="checkbox"
                checked={formHeader.attivo}
                onChange={(e) => update('attivo', e.target.checked)}
              />
              <span>Integrazione attiva</span>
            </label>
            <small className="ai-field-hint">
              Se disattivata, il lookup nominativi non userà OpenApi.
            </small>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Ambiente attivo</label>
            <div className="openapi-env-toggle">
              <button
                type="button"
                className={`openapi-env-btn${formHeader.ambienteAttivo === 'SANDBOX' ? ' openapi-env-btn--active' : ''}`}
                onClick={() => update('ambienteAttivo', 'SANDBOX')}
              >
                Sandbox (test)
              </button>
              <button
                type="button"
                className={`openapi-env-btn${formHeader.ambienteAttivo === 'PRODUZIONE' ? ' openapi-env-btn--active' : ''}`}
                onClick={() => update('ambienteAttivo', 'PRODUZIONE')}
              >
                Produzione
              </button>
            </div>
            <small className="ai-field-hint">
              L'ambiente attivo viene usato per il lookup, la verifica credenziali
              e l'auto-refresh dei token.
            </small>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Email account OpenApi</label>
            <input
              type="email"
              className="bop-input"
              value={formHeader.emailAccount}
              onChange={(e) => update('emailAccount', e.target.value)}
              placeholder="esempio@dominio.it"
            />
          </div>

          <div className="openapi-credentials-grid">
            <div className="openapi-env-col">
              <div className="openapi-env-col-header openapi-env-col-header--prod">
                <span className="openapi-env-col-header__dot" aria-hidden="true" />
                Produzione
              </div>
              <div className="bop-form-group">
                <label className="bop-label">API Key Produzione</label>
                <input
                  type="text"
                  className="bop-input"
                  value={formHeader.apiKeyProduzione}
                  onChange={(e) => update('apiKeyProduzione', e.target.value)}
                  placeholder="ApiKey..."
                />
              </div>
            </div>
            <div className="openapi-env-col">
              <div className="openapi-env-col-header openapi-env-col-header--sandbox">
                <span className="openapi-env-col-header__dot" aria-hidden="true" />
                Sandbox (test)
              </div>
              <div className="bop-form-group">
                <label className="bop-label">API Key Sandbox</label>
                <input
                  type="text"
                  className="bop-input"
                  value={formHeader.apiKeySandbox}
                  onChange={(e) => update('apiKeySandbox', e.target.value)}
                  placeholder="ApiKey..."
                />
              </div>
            </div>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Note</label>
            <textarea
              className="bop-input"
              rows={2}
              value={formHeader.note}
              onChange={(e) => update('note', e.target.value)}
              placeholder="Note opzionali..."
            />
          </div>

          {headerError && (
            <div className="openapi-feedback openapi-feedback--error">{headerError}</div>
          )}
          {headerMessage && !headerError && (
            <div className="openapi-feedback openapi-feedback--success">{headerMessage}</div>
          )}

          <div className="openapi-actions-bar">
            <button
              type="button"
              className="bop-btn-primary"
              onClick={onSaveHeader}
              disabled={savingHeader}
            >
              {savingHeader ? 'Salvataggio...' : 'Salva'}
            </button>
            <button
              type="button"
              className="bop-btn-secondary"
              onClick={onVerifica}
              disabled={verificaInCorso || !config}
              title={!config ? 'Salva prima la configurazione' : 'Verifica credenziali e leggi credito'}
            >
              {verificaInCorso ? 'Verifica in corso...' : 'Verifica credenziali e leggi credito'}
            </button>

            {config?.creditoUltimo != null && (
              <span className="openapi-credit-display">
                <span className="openapi-credit-display__value">{config.creditoUltimo}</span>
                <span>
                  crediti — letto il {formatDateTime(config.creditoUltimoAt)}
                  {config.creditoAmbienteUltimo ? ` per ambiente ${config.creditoAmbienteUltimo}` : ''}
                </span>
              </span>
            )}
          </div>

          {verificaResult && (
            <div className={`openapi-test-result${verificaResult.ok ? ' openapi-test-result--success' : ' openapi-test-result--error'}`}>
              <span>
                {verificaResult.latenzaMs}ms — HTTP {verificaResult.httpStatus} — {verificaResult.messaggio}
                {verificaResult.creditoResiduo != null ? ` — Crediti: ${verificaResult.creditoResiduo}` : ''}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Zona 2: Tabella servizi ────────────────────────────────────── */}
      <div className="bop-card">
        <div className="openapi-servizi-table-header">
          <h3 className="bop-card__title bop-mb-0">Servizi configurati</h3>
          <button
            type="button"
            className="bop-btn-primary"
            onClick={onAdd}
            disabled={!config}
            title={!config ? 'Salva prima la configurazione' : 'Aggiungi servizio'}
          >
            + Aggiungi servizio
          </button>
        </div>
        <div className="bop-card__body">
          {serviziError && (
            <div className="openapi-feedback openapi-feedback--error">{serviziError}</div>
          )}
          {serviziMessage && !serviziError && (
            <div className="openapi-feedback openapi-feedback--success">{serviziMessage}</div>
          )}

          {servizi.length === 0 ? (
            <div className="bop-empty-state">
              <p className="bop-empty-state__title">Nessun servizio configurato</p>
              <p>Aggiungi un servizio dal catalogo per iniziare.</p>
            </div>
          ) : (
            <div className="bop-table-wrapper">
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Servizio</th>
                    <th>Stato</th>
                    <th>Token Sandbox</th>
                    <th>Token Produzione</th>
                    <th className="bop-text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {servizi.map((s) => {
                    const sandbox = tokenBadgeInfo(s.tokenIdSandbox, s.tokenScadenzaSandbox);
                    const prod = tokenBadgeInfo(s.tokenIdProduzione, s.tokenScadenzaProduzione);
                    const busy = busyServizioId === s.id;
                    return (
                      <tr key={s.id}>
                        <td>
                          <div>
                            <strong>{s.catalogo.etichetta}</strong>
                          </div>
                          <small className="bop-text-muted">{s.codiceCatalogo}</small>
                        </td>
                        <td>
                          <span className={`bop-badge ${s.attivo ? 'bop-badge-success' : 'bop-badge-neutral'}`}>
                            {s.attivo ? 'attivo' : 'disattivo'}
                          </span>
                        </td>
                        <td>
                          <span className={`openapi-token-badge openapi-token-badge--${sandbox.variant}`}>
                            <span className="openapi-token-badge__label">{sandbox.label}</span>
                            {sandbox.date && (
                              <span className="openapi-token-badge__date">scade il {sandbox.date}</span>
                            )}
                          </span>
                        </td>
                        <td>
                          <span className={`openapi-token-badge openapi-token-badge--${prod.variant}`}>
                            <span className="openapi-token-badge__label">{prod.label}</span>
                            {prod.date && (
                              <span className="openapi-token-badge__date">scade il {prod.date}</span>
                            )}
                          </span>
                        </td>
                        <td className="bop-text-right">
                          <BopRowActions
                            actions={[
                              {
                                icon: <IconEdit />,
                                label: 'Modifica',
                                onClick: () => onEdit(s),
                                variant: 'primary',
                                disabled: busy,
                              },
                              {
                                icon: <IconRefresh />,
                                label: 'Genera token Sandbox',
                                onClick: () => onGeneraToken(s, 'SANDBOX'),
                                disabled: busy,
                              },
                              {
                                icon: <IconRefreshFilled />,
                                label: 'Genera token Produzione',
                                onClick: () => onGeneraToken(s, 'PRODUZIONE'),
                                disabled: busy,
                              },
                              {
                                icon: <IconCheck />,
                                label: 'Keepalive Sandbox',
                                onClick: () => onKeepalive(s, 'SANDBOX'),
                                disabled: busy,
                              },
                              {
                                icon: <IconCheckFilled />,
                                label: 'Keepalive Produzione',
                                onClick: () => onKeepalive(s, 'PRODUZIONE'),
                                disabled: busy,
                              },
                              {
                                icon: <IconTrash />,
                                label: 'Elimina',
                                onClick: () => onDelete(s),
                                variant: 'danger',
                                disabled: busy,
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modale servizio (Zone 3)
// ─────────────────────────────────────────────────────────────────────────────
interface ModaleServizioProps {
  mode: 'add' | 'edit';
  form: FormServizio;
  setForm: React.Dispatch<React.SetStateAction<FormServizio>>;
  onChangeCodiceCatalogo: (codice: string) => void;
  catalogo: ServizioCatalogo[];
  saving: boolean;
  error: string;
  onClose: () => void;
  onSave: () => void;
}

function ModaleServizio({
  mode, form, setForm, onChangeCodiceCatalogo, catalogo,
  saving, error, onClose, onSave,
}: ModaleServizioProps) {
  const cataloghiAttivi = catalogo.filter((c) => c.attivoNelCatalogo);
  const itemSelezionato = catalogo.find((c) => c.codice === form.codiceCatalogo);

  const update = <K extends keyof FormServizio>(key: K, value: FormServizio[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="bop-modal-overlay" onClick={onClose}>
      <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bop-modal__header">
          <h3 className="bop-modal__title">
            {mode === 'add' ? 'Aggiungi servizio' : 'Modifica servizio'}
          </h3>
          <button type="button" className="bop-modal__close" onClick={onClose} aria-label="Chiudi">×</button>
        </div>
        <div className="bop-modal__body">
          <div className="bop-form-group">
            <label className="bop-label">Servizio dal catalogo</label>
            <select
              className="bop-select"
              value={form.codiceCatalogo}
              onChange={(e) => onChangeCodiceCatalogo(e.target.value)}
              disabled={mode === 'edit'}
            >
              <option value="">Seleziona...</option>
              {cataloghiAttivi.map((c) => (
                <option key={c.codice} value={c.codice}>
                  {c.etichetta} ({c.codice})
                </option>
              ))}
            </select>
          </div>

          {itemSelezionato && (
            <div className="openapi-catalogo-info">
              {itemSelezionato.descrizione && (
                <div className="openapi-catalogo-info__row">
                  <span className="openapi-catalogo-info__label">Descrizione:</span>
                  <span>{itemSelezionato.descrizione}</span>
                </div>
              )}
              <div className="openapi-catalogo-info__row">
                <span className="openapi-catalogo-info__label">Scope:</span>
                <span>{itemSelezionato.scopeRichiesto}</span>
              </div>
              <div className="openapi-catalogo-info__row">
                <span className="openapi-catalogo-info__label">Crediti per chiamata:</span>
                <span>{itemSelezionato.numeroCrediti}</span>
              </div>
            </div>
          )}

          <div className="bop-form-group">
            <label className="bop-label">URL Produzione</label>
            <input
              type="text"
              className="bop-input"
              value={form.urlProduzione}
              onChange={(e) => update('urlProduzione', e.target.value)}
              placeholder="https://..."
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">URL Sandbox</label>
            <input
              type="text"
              className="bop-input"
              value={form.urlSandbox}
              onChange={(e) => update('urlSandbox', e.target.value)}
              placeholder="https://..."
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Note</label>
            <textarea
              className="bop-input"
              rows={2}
              value={form.note}
              onChange={(e) => update('note', e.target.value)}
              placeholder="Note opzionali..."
            />
          </div>

          <div className="bop-form-group">
            <label className="ai-checkbox-label">
              <input
                type="checkbox"
                checked={form.attivo}
                onChange={(e) => update('attivo', e.target.checked)}
              />
              <span>Servizio attivo</span>
            </label>
          </div>

          {error && (
            <div className="openapi-feedback openapi-feedback--error">{error}</div>
          )}
        </div>
        <div className="bop-modal__footer">
          <button type="button" className="bop-btn-secondary" onClick={onClose} disabled={saving}>
            Annulla
          </button>
          <button type="button" className="bop-btn-primary" onClick={onSave} disabled={saving}>
            {saving ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sezione Log (invariata)
// ─────────────────────────────────────────────────────────────────────────────
interface SezioneLogProps {
  loading: boolean;
  logEntries: LogEntry[];
  logFilters: { dal: string; al: string };
  setLogFilters: React.Dispatch<React.SetStateAction<{ dal: string; al: string }>>;
  pagination: { page: number; limit: number; total: number; totalPages: number };
  setPagination: React.Dispatch<
    React.SetStateAction<{ page: number; limit: number; total: number; totalPages: number }>
  >;
}

function SezioneLog({
  loading, logEntries, logFilters, setLogFilters,
  pagination, setPagination,
}: SezioneLogProps) {
  return (
    <>
      {/* Filtri */}
      <div className="bop-card">
        <div className="ai-log-filters">
          <div className="ai-log-filter-field-sm">
            <label className="bop-label">Dal</label>
            <input
              type="date"
              className="bop-input"
              value={logFilters.dal}
              onChange={(e) => setLogFilters({ ...logFilters, dal: e.target.value })}
            />
          </div>
          <div className="ai-log-filter-field-sm">
            <label className="bop-label">Al</label>
            <input
              type="date"
              className="bop-input"
              value={logFilters.al}
              onChange={(e) => setLogFilters({ ...logFilters, al: e.target.value })}
            />
          </div>
          <button
            className="bop-btn-secondary"
            onClick={() => {
              setLogFilters({ dal: '', al: '' });
              setPagination((p) => ({ ...p, page: 1 }));
            }}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Tabella */}
      <div className="bop-card">
        {loading ? (
          <div className="ai-loading">
            <p className="ai-loading-text">Caricamento log...</p>
          </div>
        ) : logEntries.length === 0 ? (
          <div className="ai-empty-state">
            <h3 className="ai-empty-title">Nessuna chiamata registrata</h3>
            <p className="ai-empty-text">
              Non ci sono chiamate OpenApi per i filtri selezionati.
            </p>
          </div>
        ) : (
          <>
            <div className="bop-table-wrapper">
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Servizio</th>
                    <th>Ambiente</th>
                    <th>Codice cercato</th>
                    <th className="bop-text-right">HTTP</th>
                    <th className="bop-text-right">Durata (ms)</th>
                    <th>Esito</th>
                    <th>Cache</th>
                  </tr>
                </thead>
                <tbody>
                  {logEntries.map((entry) => (
                    <tr key={entry.id}>
                      <td className="ai-log-date">{formatDateTime(entry.createdAt)}</td>
                      <td>{entry.servizio}</td>
                      <td>
                        <span
                          className={`bop-badge ${
                            entry.ambiente === 'PRODUZIONE'
                              ? 'bop-badge-warning'
                              : 'bop-badge-info'
                          }`}
                        >
                          {entry.ambiente}
                        </span>
                      </td>
                      <td className="ai-log-model">{entry.codice}</td>
                      <td className="ai-log-number">{entry.httpStatus ?? '-'}</td>
                      <td className="ai-log-number">{entry.durataMs ?? '-'}</td>
                      <td>
                        {entry.esitoOk ? (
                          <span className="ai-log-status success" title="OK">
                            ✓ OK
                          </span>
                        ) : (
                          <span
                            className="ai-log-status error"
                            title={entry.erroreMessaggio || undefined}
                          >
                            ✗ Errore
                          </span>
                        )}
                      </td>
                      <td>
                        {entry.fontecache ? (
                          <span className="bop-badge bop-badge-success">✓ cache</span>
                        ) : (
                          <span className="bop-text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="ai-pagination">
                <button
                  className="bop-btn-secondary"
                  onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  Precedente
                </button>
                <span className="ai-pagination-info">
                  Pagina {pagination.page} di {pagination.totalPages}
                </span>
                <button
                  className="bop-btn-secondary"
                  onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Successiva
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Icone azioni riga (16x16)
// ─────────────────────────────────────────────────────────────────────────────
const IconEdit = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const IconTrash = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const IconRefresh = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const IconRefreshFilled = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M17.65 6.35A7.96 7.96 0 0 0 12 4a8 8 0 1 0 7.75 10h-2.08A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
  </svg>
);

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconCheckFilled = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8z" />
  </svg>
);
